import React from "react";
import { Plus, Minus } from "lucide-react";

const diffDot = {
  Easy: "bg-emerald-400",
  Medium: "bg-amber-400",
  Hard: "bg-rose-500",
};
const diffLabel = {
  Easy: "text-emerald-600 dark:text-emerald-400",
  Medium: "text-amber-600 dark:text-amber-400",
  Hard: "text-rose-600 dark:text-rose-400",
};
const diffTime = { Easy: 10, Medium: 20, Hard: 30 };

const ProblemCard = ({ problem, action, onAction, onViewDetails, compact = false, selfHosted = false }) => {
  const isAdd = action === "add";
  const mins = problem.estimated_time ?? diffTime[problem.difficulty] ?? null;

  if (compact) {
    return (
      <div
        onClick={() => onViewDetails?.(problem)}
        className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/20 hover:shadow-sm transition-all duration-200 cursor-pointer group"
      >
        {/* Difficulty + Custom pill badges */}
        <div className="flex flex-col gap-1 flex-shrink-0">
          {problem.difficulty && (
            <span
              className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                problem.difficulty === "Easy"
                  ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                  : problem.difficulty === "Hard"
                  ? "bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400"
                  : "bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400"
              }`}
            >
              {problem.difficulty}
            </span>
          )}
          {selfHosted && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400">
              Custom
            </span>
          )}
        </div>

        {/* Title + meta */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold dark:text-white text-textPrimary truncate leading-tight">
            {problem.title}
          </p>
          {problem.track === "apex" && problem.topics?.length > 0 ? (
            <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 block truncate" title={problem.topics.join(", ")}>
              {problem.topics.slice(0, 2).join(" · ")}{problem.topics.length > 2 ? ` +${problem.topics.length - 2}` : ""}
            </span>
          ) : mins ? (
            <span className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 block">{mins}m</span>
          ) : null}
        </div>

        {/* Action button */}
        <button
          onClick={(e) => { e.stopPropagation(); onAction(problem); }}
          className={`w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center transition-all duration-150 active:scale-90 border ${
            isAdd
              ? "text-emerald-500 bg-emerald-50 dark:bg-emerald-500/15 border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100 dark:hover:bg-emerald-500/25"
              : "text-rose-500 bg-rose-50 dark:bg-rose-500/15 border-rose-200 dark:border-rose-500/30 hover:bg-rose-100 dark:hover:bg-rose-500/25"
          }`}
          title={isAdd ? "Add to set" : "Remove from set"}
        >
          {isAdd ? <Plus size={12} strokeWidth={2.5} /> : <Minus size={12} strokeWidth={2.5} />}
        </button>
      </div>
    );
  }

  return (
    <div
      onClick={() => onViewDetails?.(problem)}
      className="flex flex-col p-3 rounded-xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.06] hover:border-gray-300 dark:hover:border-white/20 hover:shadow-sm transition-all duration-200 cursor-pointer"
    >
      {problem.difficulty ? (
        <div className="flex items-center gap-1.5 mb-2">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${diffDot[problem.difficulty] ?? "bg-gray-300"}`} />
          <span className={`text-[10px] font-bold uppercase tracking-wider ${diffLabel[problem.difficulty] ?? "text-gray-400"}`}>
            {problem.difficulty}
          </span>
        </div>
      ) : (
        <div className="mb-2 h-4" />
      )}
      <p className="text-xs font-semibold dark:text-white text-textPrimary leading-snug flex-1 mb-3">
        {problem.title}
      </p>
      <div className="flex items-center justify-between mt-auto">
        <span className="text-[11px] text-gray-400 dark:text-gray-500">
          {mins ? `${mins}m` : ""}
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); onAction(problem); }}
          className={`w-6 h-6 rounded-full flex items-center justify-center transition-all duration-150 active:scale-95 border ${
            isAdd
              ? "text-emerald-500 bg-emerald-50 dark:bg-emerald-500/15 border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100 dark:hover:bg-emerald-500/25"
              : "text-rose-500 bg-rose-50 dark:bg-rose-500/15 border-rose-200 dark:border-rose-500/30 hover:bg-rose-100 dark:hover:bg-rose-500/25"
          }`}
          title={isAdd ? "Add to set" : "Remove from set"}
        >
          {isAdd ? <Plus size={12} strokeWidth={2.5} /> : <Minus size={12} strokeWidth={2.5} />}
        </button>
      </div>
    </div>
  );
};

export default ProblemCard;
